import { createRouter } from "@tanstack/react-router";
import { DefaultCatchBoundary } from "./components/DefaultCatchBoundary.tsx";
import { NotFound } from "./components/NotFound.tsx";
import { getDomainForRoutePrefix, getRoutePrefixForHostname, isDomainPath, LOCAL_DOMAINS } from "./config/domains.ts";
import { routeTree } from "./routeTree.gen.ts";

/**
 * Input rewrite: prepend the matching domain prefix to the pathname when the
 * request arrives on a sub-app hostname and the path lives in that sub-app.
 *
 * Idempotent: if the pathname is already prefixed (e.g. `/orca/projects` on
 * `orca.test`), this is a no-op. The router can re-invoke the rewrite on
 * already-rewritten URLs during client-side navigation; without this guard
 * the prefix would compound to `/orca/orca/orca/...`.
 *
 * @since 1.0.0
 */
export const inputRewrite = ({ url }: { url: URL }): URL => {
  const hostname = import.meta.env.VITE_FORCE_DOMAIN || url.hostname;
  const prefix = getRoutePrefixForHostname(hostname);
  if (!prefix || !isDomainPath(prefix, url.pathname)) return url;
  if (url.pathname === prefix || url.pathname.startsWith(prefix + "/")) return url;
  url.pathname = prefix + url.pathname;
  return url;
};

/**
 * Output rewrite: strip the internal prefix from generated URLs and set the
 * canonical hostname so `<Link>` outputs clean cross-app URLs.
 *
 * On hosts in `LOCAL_DOMAINS` the canonical-hostname rewrite is suppressed
 * (so `.test` hosts don't redirect to production), but the prefix still
 * gets stripped when the link targets the same sub-app as the current host
 * — otherwise navigating internally on `orca.test` would put `/orca/...`
 * into the address bar, and that stale URL would feed straight back into
 * `inputRewrite` on the next click.
 *
 * Cross-app links on a local host (e.g. `<Link to="/clawinstall/admin">`
 * generated while the user is on `orca.test`) keep their prefix so the
 * link still resolves on the current local hostname.
 *
 * @since 1.0.0
 */
export const outputRewrite = ({ url }: { url: URL }): URL => {
  const currentHostname = typeof window !== "undefined" ? window.location.hostname : url.hostname;
  const match = getDomainForRoutePrefix(url.pathname);
  if (!match) return url;

  const currentPrefix = getRoutePrefixForHostname(currentHostname);
  const sameApp = currentPrefix === match.prefix;
  const isLocal = LOCAL_DOMAINS.has(currentHostname);

  if (isLocal) {
    if (sameApp) {
      url.pathname = url.pathname.slice(match.prefix.length) || "/";
    }
    return url;
  }

  if (!sameApp) {
    url.hostname = match.domain;
  }
  url.pathname = url.pathname.slice(match.prefix.length) || "/";
  return url;
};

export function getRouter() {
  const router = createRouter({
    routeTree,
    defaultPreload: "intent",
    defaultErrorComponent: DefaultCatchBoundary,
    defaultNotFoundComponent: NotFound,
    scrollRestoration: true,
    rewrite: {
      input: inputRewrite,
      output: outputRewrite,
    },
  });
  return router;
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof getRouter>;
  }
}
